import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import SubstackCTA from "@/components/SubstackCTA";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
  }, []);

  const categories = ["All", ...Array.from(new Set(articles.map((a) => a.category)))];
  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);
  const [lead, ...rest] = filtered;

  return (
    <div data-testid="articles-page">
      <Seo title="Articles" description="Rigorous, human-centered journalism connecting lived experience to systemic trends." />
      <section className="pt-[120px] pb-12 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
        <p className="label text-ink/50 mb-6">The Reporting</p>
        <RevealLines
          as="h1"
          className="font-display font-black text-[14vw] lg:text-[8vw] leading-[0.85] tracking-tight"
          lines={["The", "archive."]}
        />
        <FadeUp delay={0.5}>
          <p className="mt-8 max-w-xl text-lg text-ink/70 leading-relaxed">
            Every story we've published — reporting, analysis, and community storytelling from
            across Colorado and beyond.
          </p>
        </FadeUp>

        {/* Filters */}
        <FadeUp delay={0.15}>
          <div className="flex flex-wrap gap-3 mt-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`label px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                  active === c ? "bg-ink text-white border-ink" : "border-ink/30 text-ink/60 hover:border-ink hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-24">
        {lead && (
          <FadeUp>
            <Link to={`/article/${lead.id}`} data-testid="articles-lead-card" className="grid lg:grid-cols-2 gap-8 lg:gap-14 mb-16 group items-center border-t border-ink/15 pt-12">
              <div className="hover-img aspect-[16/11] bg-paper">
                <img src={lead.cover_image} alt={lead.title} className="h-full w-full object-cover" />
              </div>
              <div>
                <p className="label text-ink/60 mb-4">{lead.category} · {lead.read_time} min read</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight group-hover:text-ink/70 transition-colors">{lead.title}</h2>
                <p className="mt-5 text-lg text-ink/70 leading-relaxed max-w-xl">{lead.excerpt}</p>
                <span className="inline-flex items-center gap-2 mt-7 label link-underline pb-1">Read the story <ArrowUpRight size={15} /></span>
              </div>
            </Link>
          </FadeUp>
        )}

        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 border-t border-ink/15 pt-12">
          {rest.map((a) => (
            <StaggerItem key={a.id}>
              <Link to={`/article/${a.id}`} data-testid={`article-card-${a.id}`} className="group block">
                <div className="hover-img aspect-[4/3] bg-paper mb-5">
                  <img src={a.cover_image} alt={a.title} className="h-full w-full object-cover" />
                </div>
                <p className="label text-ink/50 mb-2">{a.category} · {a.read_time} min</p>
                <h3 className="font-display text-2xl font-bold leading-snug group-hover:text-ink/60 transition-colors">{a.title}</h3>
                <p className="mt-3 text-ink/70 leading-relaxed line-clamp-2">{a.excerpt}</p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>

        {filtered.length === 0 && <p className="text-ink/50 mt-10">No articles in this category yet.</p>}
      </section>

      <SubstackCTA />
    </div>
  );
}
